import { Alert, Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../../api/biApi";
import type { SmartViewArtifact } from "../../../../../api/types/smartViewArtifactsTypes";
import { deleteArtifacts } from "./deleteArtifacts";

interface FailedRun {
  failedCount: number;
  totalCount: number;
}

const deleteArtifactRequest = withErrorHandling(biApi.smartViewArtifacts.delete);

const deleteArtifact = async (artifactId: string): Promise<void> => {
  const [, error] = await deleteArtifactRequest(artifactId);
  if (!error) {
    return;
  }
  if (!error.isCanceledRequest) {
    logError(error, "[DeleteArtifactsDialog] deleteArtifact");
  }
  throw new Error(error.message);
};

const getHeading = (artifacts: SmartViewArtifact[]): string => {
  const [firstArtifact] = artifacts;
  return artifacts.length === 1 && firstArtifact
    ? `Delete ${firstArtifact.name}?`
    : `Delete ${artifacts.length} dashboards?`;
};

const getFailureText = ({ failedCount, totalCount }: FailedRun): string =>
  totalCount === 1 ? "Failed to delete the dashboard" : `Failed to delete ${failedCount} of ${totalCount} dashboards`;

interface Props {
  artifacts: SmartViewArtifact[];
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteArtifactsDialog = ({ artifacts, onClose, onDeleted }: Props) => {
  const [shownArtifacts, setShownArtifacts] = useState(artifacts);
  const [failedRun, setFailedRun] = useState<FailedRun>();
  const [isDeleting, setIsDeleting] = useState(false);

  const isSingle = shownArtifacts.length === 1;

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const failedIds = await deleteArtifacts(
      shownArtifacts.map((artifact) => artifact.id),
      deleteArtifact
    );
    setIsDeleting(false);
    if (failedIds.length < shownArtifacts.length) {
      onDeleted();
    }
    if (failedIds.length === 0) {
      onClose();
      return;
    }
    setFailedRun({ failedCount: failedIds.length, totalCount: shownArtifacts.length });
    setShownArtifacts(shownArtifacts.filter((artifact) => failedIds.includes(artifact.id)));
  };

  return (
    <SmallDialog open onClose={handleClose}>
      <DialogTitle>{isSingle ? "Delete Dashboard" : "Delete Dashboards"}</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center", textAlign: "center" }}>
          {failedRun && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {getFailureText(failedRun)}
            </Alert>
          )}
          <DeleteDialogIcon sx={{ fontSize: 80, color: "white" }} />
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            {getHeading(shownArtifacts)}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: "normal" }}>
            {isSingle
              ? "You're about to delete this dashboard. This action is permanent and can't be undone."
              : "You're about to delete these dashboards. This action is permanent and can't be undone."}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelDisabled={isDeleting}>
        <Button variant="outlined" color="error" loading={isDeleting} disabled={isDeleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteArtifactsDialog;
